import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardContent, Box, Typography, LinearProgress, Table, TableHead, TableRow, TableCell, TableBody, Button, Chip, Alert } from '@mui/material';
import { httpsCallable } from 'firebase/functions';

const statusColor = (s) => {
    switch ((s || '').toString()) {
        case 'pending': return 'default';
        case 'sending': return 'info';
        case 'sent': return 'success';
        case 'failed': return 'error';
        case 'partial': return 'warning';
        case 'rate_limited': return 'warning';
        case 'unknown': return 'default';
        default: return 'default';
    }
};

// No local sample data — component will use real backend shape: { campaign, batches, logs }

const DeliveryDashboard = ({ functionsInstance }) => {
    const [data, setData] = useState({ campaign: {}, batches: [], logs: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchStatus = async () => {
        setError(null);
        setLoading(true);
        try {
            if (functionsInstance) {
                const fn = httpsCallable(functionsInstance, 'getDeliveryStatus');
                const resp = await fn({});
                // server returns { success: true, data: { campaign, batches, logs } }
                const payload = resp && resp.data ? (resp.data.data || resp.data) : null;
                setData(payload || { campaign: {}, batches: [], logs: [] });
            } else {
                setData({ campaign: {}, batches: [], logs: [] });
            }
        } catch (e) {
            console.warn('fetchStatus failed', e);
            setError(e && e.message ? e.message : String(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatus();
        const iv = setInterval(fetchStatus, 15000);
        return () => clearInterval(iv);
    }, [functionsInstance]);

    const handleRetryBatch = async (batchId) => {
        setError(null);
        try {
            if (functionsInstance) {
                const fn = httpsCallable(functionsInstance, 'retryBatch');
                await fn({ campaignId: data.campaign && data.campaign.id, batchId });
                await fetchStatus();
            } else {
                setData(d => ({
                    ...d,
                    batches: d.batches.map(b => b.id === batchId ? { ...b, status: 'sending' } : b)
                }));
            }
        } catch (e) {
            console.warn('retryBatch failed', e);
            setError(e && e.message ? e.message : String(e));
        }
    };

    const handleRetryRecipient = async (recipientId) => {
        setError(null);
        try {
            if (functionsInstance) {
                const fn = httpsCallable(functionsInstance, 'retryRecipient');
                await fn({ campaignId: data.campaign && data.campaign.id, recipientId });
                await fetchStatus();
            } else {
                setData(d => ({
                    ...d,
                    logs: d.logs.map(l => l.id === recipientId ? { ...l, status: 'pending', detail: 'Queued for retry' } : l)
                }));
            }
        } catch (e) {
            console.warn('retryRecipient failed', e);
            setError(e && e.message ? e.message : String(e));
        }
    };

    const { campaign, batches, logs } = data || {};
    const campaignSent = (campaign && (campaign.sent || campaign.total_sent || 0)) || 0;
    const campaignTotal = (campaign && (campaign.total || campaign.total_recipients || 0)) || 0;
    const campaignFailed = (campaign && (campaign.total_failed || 0)) || 0;
    const progress = campaignTotal ? Math.min(100, Math.round((Number(campaignSent) / Number(campaignTotal)) * 100)) : 0;

    // If backend returned no batch docs but campaign contains batch ids, create placeholders
    // Normalize batches: server may return array of objects or campaign may only contain ids
    const effectiveBatches = (Array.isArray(batches) && batches.length && typeof batches[0] === 'object') ? batches : (campaign && Array.isArray(campaign.batches) ? campaign.batches.map(id => ({ id, status: 'unknown', total: 0, sent: 0, failed: 0, pending: 0 })) : []);

    // Normalize logs and sort by time desc. Support numeric epoch ms or Firestore Timestamp
    const normalizeTime = (t) => {
        if (!t) return null;
        if (typeof t === 'number') return t;
        if (t && typeof t.toDate === 'function') return t.toDate().getTime();
        return null;
    };
    const effectiveLogs = Array.isArray(logs) ? logs.map(l => ({ ...l, time: normalizeTime(l.time) || normalizeTime(l.at) || null })) : [];
    effectiveLogs.sort((a, b) => (b.time || 0) - (a.time || 0));

    const formatTime = (t) => { if (!t) return '—'; try { return new Date(Number(t)).toLocaleString(); } catch (e) { return '—'; } };

    return (
        <Card>
            <CardHeader title="Delivery Dashboard" subheader="Campaign and delivery status" />
            <CardContent>
                {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Campaign</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{campaign && campaign.message ? campaign.message : ''}</Typography>
                    <Typography variant="caption" color="text.secondary">Created: {campaign && campaign.created_at ? new Date(campaign.created_at).toLocaleString() : '—'} • Scheduled: {campaign && campaign.scheduled_at ? new Date(campaign.scheduled_at).toLocaleString() : '—'}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <LinearProgress variant="determinate" value={progress} sx={{ height: 12, borderRadius: 1 }} />
                        </Box>
                        <Box sx={{ minWidth: 200, textAlign: 'right' }}>
                            <Typography variant="body2">
                                {campaign ? `${Number(campaign.total_sent || campaignSent).toLocaleString()} / ${Number(campaign.total_recipients || campaignTotal).toLocaleString()} sent` : '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">Status: {campaign && campaign.status ? campaign.status : '—'}</Typography>
                            {campaign && Number(campaign.total_failed || campaignFailed) > 0 && (
                                <Typography variant="caption" color="error" sx={{ display: 'block' }}>Failed: {Number(campaign.total_failed || campaignFailed).toLocaleString()}</Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{p:1}}>Sent at: {campaign ? formatTime(campaign.sent_at || campaign.completed_at) : '—'}</Typography>
                            <Box sx={{ mt: 1 }}>
                                <Button size="small" onClick={fetchStatus} disabled={loading}>Refresh</Button>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Batches</Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Batch ID</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Progress</TableCell>
                                <TableCell>Stats</TableCell>
                                <TableCell>Started</TableCell>
                                <TableCell>Completed</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {effectiveBatches && effectiveBatches.map(b => (
                                <TableRow key={b.id}>
                                    <TableCell>{b.id}</TableCell>
                                    <TableCell><Chip label={b.status} color={statusColor(b.status)} size="small" /></TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{b.total ? `${b.sent}/${b.total}` : '—'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">Sent: {b.sent} • Failed: {b.failed} • Pending: {b.pending}</Typography>
                                    </TableCell>
                                    <TableCell>{b.started_at ? formatTime(b.started_at) : '—'}</TableCell>
                                    <TableCell>{b.completed_at ? formatTime(b.completed_at) : '—'}</TableCell>
                                    <TableCell align="right">
                                        {b.status !== 'sending' && (
                                            <Button size="small" onClick={() => handleRetryBatch(b.id)}>Retry</Button>
                                        )}
                                        {b.status === 'sending' && (
                                            <Button size="small" onClick={() => handleRetryBatch(b.id)}>Pause/Resume</Button>
                                        )}
                                        {b.rate_limit_reset && (
                                            <Typography variant="caption" color="warning" sx={{ display: 'block' }}>Rate reset: {formatTime(b.rate_limit_reset)}</Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>

                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Per-recipient delivery logs</Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Recipient</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Details</TableCell>
                                <TableCell>Batch</TableCell>
                                <TableCell>Time</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {effectiveLogs && effectiveLogs.slice(0, 500).map(l => (
                                <TableRow key={l.id || `${l.recipient}-${l.time || ''}`}>
                                    <TableCell>{l.recipient || l.id}</TableCell>
                                    <TableCell><Chip label={l.status} color={statusColor(l.status)} size="small" /></TableCell>
                                    <TableCell style={{ maxWidth: 300 }}>{l.detail || '—'}</TableCell>
                                    <TableCell>{l.batchId}</TableCell>
                                    <TableCell>{formatTime(l.time)}</TableCell>
                                    <TableCell align="right">
                                        {l.status !== 'sent' && (
                                            <Button size="small" onClick={() => handleRetryRecipient(l.id || l.recipient)}>Retry</Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            </CardContent>
        </Card>
    );
};

export default DeliveryDashboard;
